import React, { useEffect, useState } from 'react';
import { collection, query, orderBy, onSnapshot, deleteDoc, doc, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

const PRIORITY_MAP = {
    important: { label: '🔴 Чухал', color: '#ef4444', emoji: '🔴' },
    official: { label: '🔵 Албаны', color: '#3b82f6', emoji: '🔵' },
    regular: { label: '🟢 Энгийн', color: '#10b981', emoji: '🟢' },
};

function RequestsPage({ user, userData }) {
    const teamId = userData?.teamId;
    const [meetingRequests, setMeetingRequests] = useState([]);
    const [activeRequestTab, setActiveRequestTab] = useState('important');
    const [showAddRequest, setShowAddRequest] = useState(false);
    const [newRequestTitle, setNewRequestTitle] = useState('');
    const [newRequestDesc, setNewRequestDesc] = useState('');
    const [convertModal, setConvertModal] = useState(null);
    const [convertDate, setConvertDate] = useState('');
    const [convertTime, setConvertTime] = useState('');

    const [ordinationRequests, setOrdinationRequests] = useState([]);
    const [showAddOrdination, setShowAddOrdination] = useState(false);
    const [ordForm, setOrdForm] = useState({
        lastName: '', firstName: '', phone: '', age: '', sex: '',
        profession: '', job: '', birthplace: '', monastery: ''
    });

    const [activeSection, setActiveSection] = useState('meeting'); // 'meeting' | 'ordination'

    const today = new Date();

    useEffect(() => {
        if (!teamId) return;
        const q = query(collection(db, 'teams', teamId, 'meetingRequests'), orderBy('createdAt', 'desc'));
        const unsub = onSnapshot(q, (snap) => {
            const list = [];
            snap.forEach(d => list.push({ id: d.id, ...d.data() }));
            setMeetingRequests(list);
        });
        return () => unsub();
    }, [teamId]);

    useEffect(() => {
        if (!teamId) return;
        const q = query(collection(db, 'teams', teamId, 'ordinationRequests'), orderBy('createdAt', 'desc'));
        const unsub = onSnapshot(q, (snap) => {
            const list = [];
            snap.forEach(d => list.push({ id: d.id, ...d.data() }));
            setOrdinationRequests(list);
        });
        return () => unsub();
    }, [teamId]);

    const handleAddRequest = async () => {
        if (!newRequestTitle.trim()) return;
        try {
            await addDoc(collection(db, 'teams', teamId, 'meetingRequests'), {
                title: newRequestTitle.trim(),
                description: newRequestDesc.trim(),
                priority: activeRequestTab,
                createdBy: user.uid,
                createdByName: user.displayName || 'User',
                createdAt: serverTimestamp(),
            });
            setNewRequestTitle('');
            setNewRequestDesc('');
            setShowAddRequest(false);
        } catch (err) {
            console.error('Error adding request:', err);
        }
    };

    const handleDeleteRequest = async (id) => {
        if (window.confirm('Энэ хүсэлтийг устгах уу?')) {
            try { await deleteDoc(doc(db, 'teams', teamId, 'meetingRequests', id)); } catch (err) { console.error(err); }
        }
    };

    const handleConvertToSchedule = async () => {
        if (!convertModal || !convertDate) return;
        try {
            await addDoc(collection(db, 'teams', teamId, 'schedules'), {
                title: convertModal.title,
                description: convertModal.description || '',
                date: convertDate,
                time: convertTime || '',
                meetupPerson: '',
                destination: '',
                createdBy: user.uid,
                createdByName: user.displayName || 'User',
                createdAt: serverTimestamp(),
            });
            await deleteDoc(doc(db, 'teams', teamId, 'meetingRequests', convertModal.id));
            setConvertModal(null);
            setConvertDate('');
            setConvertTime('');
        } catch (err) { console.error(err); }
    };

    const filteredRequests = meetingRequests.filter(r => r.priority === activeRequestTab);

    const handleOrdFormChange = (field, value) => setOrdForm(prev => ({ ...prev, [field]: value }));

    const handleAddOrdination = async () => {
        if (!ordForm.lastName.trim() || !ordForm.firstName.trim() || !ordForm.phone.trim()) return;
        try {
            await addDoc(collection(db, 'teams', teamId, 'ordinationRequests'), {
                ...ordForm,
                lastName: ordForm.lastName.trim(), firstName: ordForm.firstName.trim(),
                phone: ordForm.phone.trim(), age: ordForm.age.trim(), sex: ordForm.sex,
                profession: ordForm.profession.trim(), job: ordForm.job.trim(),
                birthplace: ordForm.birthplace.trim(), monastery: ordForm.monastery.trim(),
                createdBy: user.uid, createdByName: user.displayName || 'User',
                createdAt: serverTimestamp(),
            });
            setOrdForm({ lastName: '', firstName: '', phone: '', age: '', sex: '', profession: '', job: '', birthplace: '', monastery: '' });
            setShowAddOrdination(false);
        } catch (err) { console.error(err); }
    };

    const handleDeleteOrdination = async (id) => {
        if (window.confirm('Энэ хүсэлтийг устгах уу?')) {
            try { await deleteDoc(doc(db, 'teams', teamId, 'ordinationRequests', id)); } catch (err) { console.error(err); }
        }
    };

    return (
        <div className="page-container mobile-page">
            {/* Page header */}
            <div className="mobile-page-header">
                <h1 className="mobile-page-title">📋 Хүсэлт</h1>
            </div>

            {/* Section toggle */}
            <div className="section-toggle">
                <button className={`section-toggle__btn${activeSection === 'meeting' ? ' section-toggle__btn--active' : ''}`}
                    onClick={() => setActiveSection('meeting')}>
                    📋 Уулзалт <span className="section-toggle__count">{meetingRequests.length}</span>
                </button>
                <button className={`section-toggle__btn${activeSection === 'ordination' ? ' section-toggle__btn--active' : ''}`}
                    onClick={() => setActiveSection('ordination')}>
                    🙏 Сахил <span className="section-toggle__count">{ordinationRequests.length}</span>
                </button>
            </div>

            {/* Meeting Requests */}
            {activeSection === 'meeting' && (
                <div className="mr-section">
                    <div className="mr-header">
                        <h3 className="mr-title">Уулзалтын хүсэлт</h3>
                        <button className="mr-add-btn" onClick={() => setShowAddRequest(!showAddRequest)}>
                            {showAddRequest ? '✕' : '+'}
                        </button>
                    </div>

                    <div className="mr-tabs">
                        {Object.entries(PRIORITY_MAP).map(([key, val]) => (
                            <button key={key}
                                className={`mr-tab${activeRequestTab === key ? ' mr-tab--active' : ''}`}
                                style={activeRequestTab === key ? { borderBottomColor: val.color } : {}}
                                onClick={() => setActiveRequestTab(key)}>
                                {val.label}
                                <span className="mr-tab__count">{meetingRequests.filter(r => r.priority === key).length}</span>
                            </button>
                        ))}
                    </div>

                    {showAddRequest && (
                        <div className="mr-form">
                            <input className="mr-input" type="text" placeholder="Уулзалтын гарчиг..." value={newRequestTitle}
                                onChange={e => setNewRequestTitle(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleAddRequest()} autoFocus />
                            <input className="mr-input mr-input--desc" type="text" placeholder="Тайлбар (заавал биш)" value={newRequestDesc}
                                onChange={e => setNewRequestDesc(e.target.value)} />
                            <div className="mr-form__priority">
                                <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>Төрөл:</span>
                                <span style={{ color: PRIORITY_MAP[activeRequestTab].color, fontWeight: 700, fontSize: '0.8rem' }}>{PRIORITY_MAP[activeRequestTab].label}</span>
                            </div>
                            <button className="mr-submit" onClick={handleAddRequest}>Нэмэх</button>
                        </div>
                    )}

                    <div className="mr-list">
                        {filteredRequests.length === 0 ? (
                            <div className="mr-empty">{PRIORITY_MAP[activeRequestTab].emoji} Хүсэлт алга</div>
                        ) : (
                            filteredRequests.map(req => (
                                <div key={req.id} className="mr-item" style={{ borderLeftColor: PRIORITY_MAP[req.priority]?.color || '#666' }}>
                                    <div className="mr-item__content">
                                        <span className="mr-item__title">{req.title}</span>
                                        {req.description && <span className="mr-item__desc">{req.description}</span>}
                                        <span className="mr-item__meta">{req.createdByName} · {req.createdAt?.toDate ? req.createdAt.toDate().toLocaleDateString('mn-MN') : ''}</span>
                                    </div>
                                    <div className="mr-item__actions">
                                        <button className="mr-action-btn mr-action-btn--convert" title="Хуваарь руу" onClick={() => { setConvertModal(req); setConvertDate(today.toISOString().split('T')[0]); setConvertTime(''); }}>📅</button>
                                        <button className="mr-action-btn mr-action-btn--del" title="Устгах" onClick={() => handleDeleteRequest(req.id)}>🗑️</button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}

            {/* Ordination Requests */}
            {activeSection === 'ordination' && (
                <div className="mr-section ord-section">
                    <div className="mr-header">
                        <h3 className="mr-title">Сахил хүртэх хүсэлт</h3>
                        <button className="mr-add-btn" onClick={() => setShowAddOrdination(!showAddOrdination)}>
                            {showAddOrdination ? '✕' : '+'}
                        </button>
                    </div>

                    {showAddOrdination && (
                        <div className="ord-form">
                            <div className="ord-form__row">
                                <div className="ord-form__field"><label className="ord-form__label">Овог <span className="ord-req">*</span></label><input className="mr-input" type="text" placeholder="Овог" value={ordForm.lastName} onChange={e => handleOrdFormChange('lastName', e.target.value)} /></div>
                                <div className="ord-form__field"><label className="ord-form__label">Нэр <span className="ord-req">*</span></label><input className="mr-input" type="text" placeholder="Нэр" value={ordForm.firstName} onChange={e => handleOrdFormChange('firstName', e.target.value)} /></div>
                            </div>
                            <div className="ord-form__row">
                                <div className="ord-form__field"><label className="ord-form__label">Утас <span className="ord-req">*</span></label><input className="mr-input" type="tel" placeholder="99112233" value={ordForm.phone} onChange={e => handleOrdFormChange('phone', e.target.value)} /></div>
                                <div className="ord-form__field"><label className="ord-form__label">Нас</label><input className="mr-input" type="number" placeholder="Нас" value={ordForm.age} onChange={e => handleOrdFormChange('age', e.target.value)} /></div>
                            </div>
                            <div className="ord-form__row">
                                <div className="ord-form__field"><label className="ord-form__label">Хүйс <span className="ord-req">*</span></label>
                                    <select className="mr-input" value={ordForm.sex} onChange={e => handleOrdFormChange('sex', e.target.value)}><option value="">Сонгох...</option><option value="male">Эрэгтэй</option><option value="female">Эмэгтэй</option></select>
                                </div>
                                <div className="ord-form__field"><label className="ord-form__label">Мэргэжил</label><input className="mr-input" type="text" placeholder="Заавал биш" value={ordForm.profession} onChange={e => handleOrdFormChange('profession', e.target.value)} /></div>
                            </div>
                            <div className="ord-form__row">
                                <div className="ord-form__field"><label className="ord-form__label">Ажил</label><input className="mr-input" type="text" placeholder="Заавал биш" value={ordForm.job} onChange={e => handleOrdFormChange('job', e.target.value)} /></div>
                                <div className="ord-form__field"><label className="ord-form__label">Төрсөн газар <span className="ord-req">*</span></label><input className="mr-input" type="text" placeholder="Аймаг/Хот" value={ordForm.birthplace} onChange={e => handleOrdFormChange('birthplace', e.target.value)} /></div>
                            </div>
                            <div className="ord-form__field" style={{ width: '100%' }}><label className="ord-form__label">Аль хийдэд шавилан суух</label><input className="mr-input" type="text" placeholder="Хийдийн нэр (заавал биш)" value={ordForm.monastery} onChange={e => handleOrdFormChange('monastery', e.target.value)} /></div>
                            <button className="mr-submit" onClick={handleAddOrdination}>Бүртгүүлэх</button>
                        </div>
                    )}

                    <div className="mr-list">
                        {ordinationRequests.length === 0 ? (
                            <div className="mr-empty">🙏 Хүсэлт алга</div>
                        ) : (
                            ordinationRequests.map(req => (
                                <div key={req.id} className="ord-item">
                                    <div className="ord-item__content">
                                        <span className="ord-item__name">{req.lastName} {req.firstName}</span>
                                        <div className="ord-item__tags">
                                            <span className="ord-tag">📞 {req.phone}</span>
                                            {req.age && <span className="ord-tag">🎂 {req.age} нас</span>}
                                            {req.sex && <span className="ord-tag">{req.sex === 'male' ? '👨 Эр' : '👩 Эм'}</span>}
                                            {req.profession && <span className="ord-tag">🎓 {req.profession}</span>}
                                            {req.job && <span className="ord-tag">💼 {req.job}</span>}
                                            {req.birthplace && <span className="ord-tag">📍 {req.birthplace}</span>}
                                            {req.monastery && <span className="ord-tag">🏛️ {req.monastery}</span>}
                                        </div>
                                        <span className="mr-item__meta">{req.createdByName} · {req.createdAt?.toDate ? req.createdAt.toDate().toLocaleDateString('mn-MN') : ''}</span>
                                    </div>
                                    <div className="mr-item__actions">
                                        <button className="mr-action-btn mr-action-btn--del" title="Устгах" onClick={() => handleDeleteOrdination(req.id)}>🗑️</button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}

            {/* Convert Modal */}
            {convertModal && (
                <div className="mr-modal-overlay" onClick={() => setConvertModal(null)}>
                    <div className="mr-modal" onClick={e => e.stopPropagation()}>
                        <div className="mr-modal__header"><h3>📅 Хуваарь руу шилжүүлэх</h3><button className="mr-modal__close" onClick={() => setConvertModal(null)}>✕</button></div>
                        <div className="mr-modal__body">
                            <div className="mr-modal__label">Уулзалт:</div>
                            <div className="mr-modal__value" style={{ borderLeftColor: PRIORITY_MAP[convertModal.priority]?.color }}>{convertModal.title}</div>
                            <label className="mr-modal__label">Огноо: *</label>
                            <input type="date" className="mr-modal__input" value={convertDate} onChange={e => setConvertDate(e.target.value)} />
                            <label className="mr-modal__label">Цаг:</label>
                            <div className="time-picker-24h">
                                <select className="time-picker-24h__select" value={convertTime ? convertTime.split(':')[0] : ''} onChange={(e) => { const h = e.target.value; const m = convertTime ? convertTime.split(':')[1] : '00'; setConvertTime(h ? `${h}:${m}` : ''); }}>
                                    <option value="">Цаг</option>
                                    {Array.from({ length: 24 }, (_, i) => (<option key={i} value={String(i).padStart(2, '0')}>{String(i).padStart(2, '0')}</option>))}
                                </select>
                                <span className="time-picker-24h__sep">:</span>
                                <select className="time-picker-24h__select" value={convertTime ? convertTime.split(':')[1] : ''} onChange={(e) => { const h = convertTime ? convertTime.split(':')[0] : '00'; const m = e.target.value; setConvertTime(m ? `${h}:${m}` : ''); }}>
                                    <option value="">Мин</option>
                                    {Array.from({ length: 12 }, (_, i) => (<option key={i} value={String(i * 5).padStart(2, '0')}>{String(i * 5).padStart(2, '0')}</option>))}
                                </select>
                            </div>
                        </div>
                        <div className="mr-modal__footer">
                            <button className="btn btn-secondary" onClick={() => setConvertModal(null)}>Болих</button>
                            <button className="btn btn-primary" onClick={handleConvertToSchedule} disabled={!convertDate}>Хуваарь руу нэмэх</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default RequestsPage;
